<!-- src/views/vehicles/VehicleDetailsView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30">
    
    <!-- 1. PREMIUM HEADER CARD -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 no-print">
      <div class="bg-[#0A1630] rounded-[40px] p-8 border-b-4 border-[#20FCF1] shadow-2xl relative overflow-hidden">
        <div class="absolute top-0 right-0 p-12 opacity-5">
          <TruckIcon class="h-32 w-32 text-white" />
        </div>
        
        <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">Asset Registry</h1>
            <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-3">Authorised Vehicle Compliance & Monitoring</p>
          </div>
          
          <router-link to="/dashboard" class="bg-white/5 border border-white/10 text-white px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition-all flex items-center gap-2">
            <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Dashboard</span>
          </router-link>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      
      <!-- LOADING STATE -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-20">
        <svg class="animate-spin h-10 w-10 text-[#20FCF1]" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
        <p class="micro-label mt-4 text-slate-400 uppercase">Synchronising Asset Data...</p>
      </div>

      <!-- NO VEHICLE ASSIGNED -->
      <div v-else-if="!vehicle" class="bg-slate-50 p-16 rounded-[40px] border border-slate-100 text-center animate-fadeIn shadow-inner">
        <div class="bg-white w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl border border-slate-100">
          <TruckIcon class="h-10 w-10 text-slate-200" />
        </div>
        <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">No Asset Allocated</h3>
        <p class="text-slate-400 text-xs font-bold uppercase tracking-widest mt-3 max-w-sm mx-auto leading-relaxed">
          You are not currently associated with a company vehicle in the central registry.
        </p>
        <p class="text-[9px] text-slate-300 font-black uppercase tracking-[0.2em] mt-8">Contact Fleet Management for Authorisation</p>
      </div>

      <!-- VEHICLE DASHBOARD -->
      <div v-else class="space-y-10 animate-fadeIn">
        
        <!-- 1. VEHICLE OVERVIEW CARD -->
        <div class="bg-white rounded-[40px] shadow-2xl border border-slate-100 overflow-hidden">
          <div class="lg:flex">
            <!-- Asset Visual -->
            <div class="lg:w-2/5 h-64 lg:h-auto bg-[#0A1630] relative flex items-center justify-center overflow-hidden">
              <img v-if="vehicle.imageUrl" :src="vehicle.imageUrl" class="w-full h-full object-cover opacity-60" />
              <TruckIcon v-else class="h-24 w-24 text-white/10" />
              <div class="absolute inset-0 bg-gradient-to-r from-[#0A1630]/80 to-transparent"></div>
            </div>
            
            <!-- Asset Metadata -->
            <div class="p-10 lg:w-3/5 flex flex-col justify-between">
              <div>
                <div class="flex justify-between items-start mb-8">
                  <div>
                    <h2 class="text-3xl font-black text-[#0A1630] uppercase tracking-tighter leading-none">{{ vehicle.registration }}</h2>
                    <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-3">{{ vehicle.make }} {{ vehicle.model }}</p>
                  </div>
                  <span :class="statusBadge(vehicle.status).class" class="px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest shadow-sm">
                    {{ vehicle.status }}
                  </span>
                </div>
                
                <div class="grid grid-cols-2 gap-8">
                  <div class="space-y-1">
                    <span class="micro-label text-slate-400">Recorded Mileage</span>
                    <span class="text-lg font-black text-[#0A1630] uppercase">{{ vehicle.mileage ? vehicle.mileage.toLocaleString() : 'N/A' }} MI</span>
                  </div>
                  <div class="space-y-1">
                    <span class="micro-label text-slate-400">Asset Category</span>
                    <span class="text-lg font-black text-[#0A1630] uppercase">{{ vehicle.type }}</span>
                  </div>
                  <div class="space-y-1">
                    <span class="micro-label text-slate-400">Assistance Provider</span>
                    <span class="text-sm font-black text-[#0A1630] uppercase">{{ vehicle.breakdownProvider || 'Unlisted' }}</span>
                  </div>
                  <div class="space-y-1">
                    <span class="micro-label text-slate-400">Contract Reference</span>
                    <span class="text-sm font-black text-[#0A1630] uppercase">{{ vehicle.breakdownNumber || 'N/A' }}</span>
                  </div>
                </div>
              </div>

              <div class="mt-12 pt-8 border-t border-slate-50 flex flex-col sm:flex-row gap-4">
                <router-link to="/vehicle-check" class="flex-1 bg-[#20FCF1] text-[#0A1630] text-center py-5 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all shadow-xl">
                  Initialise Weekly Check
                </router-link>
                <router-link to="/dashboard" class="flex-1 bg-white border-2 border-slate-100 text-slate-400 text-center py-5 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-[#0A1630] hover:text-[#0A1630] transition-all">
                  Report Asset Issue
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- 2. COMPLIANCE MATRIX -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div class="bg-white p-8 rounded-[32px] shadow-xl border border-slate-50 flex flex-col items-center text-center">
            <span class="micro-label text-slate-300 mb-4">MOT Expiry Protocol</span>
            <span class="text-xl font-black uppercase tracking-tight" :class="getDateClass(vehicle.motExpiry)">{{ formatDate(vehicle.motExpiry) }}</span>
            <span class="text-[9px] font-black uppercase tracking-widest mt-2 px-3 py-1 rounded-full bg-slate-50" :class="getDateClass(vehicle.motExpiry)">{{ getDaysRemaining(vehicle.motExpiry) }}</span>
          </div>
          <div class="bg-white p-8 rounded-[32px] shadow-xl border border-slate-50 flex flex-col items-center text-center">
            <span class="micro-label text-slate-300 mb-4">Scheduled Service Due</span>
            <span class="text-xl font-black uppercase tracking-tight" :class="getDateClass(vehicle.serviceDue)">{{ formatDate(vehicle.serviceDue) }}</span>
            <span class="text-[9px] font-black uppercase tracking-widest mt-2 px-3 py-1 rounded-full bg-slate-50" :class="getDateClass(vehicle.serviceDue)">{{ getDaysRemaining(vehicle.serviceDue) }}</span>
          </div>
          <div class="bg-white p-8 rounded-[32px] shadow-xl border border-slate-50 flex flex-col items-center text-center">
            <span class="micro-label text-slate-300 mb-4">Tax Renewal Deadline</span>
            <span class="text-xl font-black uppercase tracking-tight" :class="getDateClass(vehicle.taxRenewal)">{{ formatDate(vehicle.taxRenewal) }}</span>
            <span class="text-[9px] font-black uppercase tracking-widest mt-2 px-3 py-1 rounded-full bg-slate-50" :class="getDateClass(vehicle.taxRenewal)">{{ getDaysRemaining(vehicle.taxRenewal) }}</span>
          </div>
        </div>

        <!-- 3. AUDIT HISTORY -->
        <div class="bg-[#0A1630] rounded-[40px] shadow-2xl p-10 text-white relative overflow-hidden">
          <div class="absolute -right-10 -bottom-10 opacity-5"><ShieldCheckIcon class="w-64 h-64 text-[#20FCF1]" /></div>
          <div class="relative z-10">
            <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#20FCF1] mb-8 flex items-center gap-3">
               <div class="w-2 h-2 rounded-full bg-[#20FCF1] animate-pulse"></div>
               Recent Audit History
            </h3>
            
            <div v-if="recentChecks.length === 0" class="py-12 text-center bg-white/5 rounded-[32px] border border-white/10">
              <p class="micro-label text-slate-500">No Check Records Identified in Registry</p>
            </div>

            <div v-else class="overflow-x-auto">
              <table class="min-w-full">
                <thead>
                  <tr class="text-[10px] font-black uppercase tracking-widest text-slate-500 text-left border-b border-white/10">
                    <th class="pb-4 font-black">Date of Submission</th>
                    <th class="pb-4 font-black">Audit Status</th>
                    <th class="pb-4 font-black text-right">Verification</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-white/5">
                  <tr v-for="check in recentChecks" :key="check.id" class="group">
                    <td class="py-6 text-xs font-bold uppercase text-slate-300">{{ formatDateTime(check.submittedAt) }}</td>
                    <td class="py-6">
                      <span class="px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest" 
                            :class="check.status === 'Pass' ? 'bg-[#20FCF1] text-[#0A1630]' : 'bg-rose-500 text-white'">
                        {{ check.status === 'Pass' ? 'Compliant' : 'Fault Flagged' }}
                      </span>
                    </td>
                    <td class="py-6 text-right">
                      <span class="text-slate-500 text-[10px] font-black uppercase tracking-widest group-hover:text-[#FD5E00] transition-colors">Recorded</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { db } from '@/firebaseConfig';
import { collection, query, where, getDocs, limit, orderBy } from 'firebase/firestore';
import { useUserStore } from '@/stores/userStore';
import { TruckIcon, ArrowUturnLeftIcon, ShieldCheckIcon } from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const loading = ref(true);
const vehicle = ref(null);
const recentChecks = ref([]);

/**
 * Status badge styling mappings
 */
const statusBadge = (status) => {
    switch(status) {
        case 'Active': return { class: 'bg-[#EBFDF8] text-[#20FCF1]' };
        case 'Maintenance': return { class: 'bg-amber-100 text-amber-600' };
        case 'SORN': return { class: 'bg-rose-100 text-rose-600' };
        default: return { class: 'bg-slate-100 text-slate-400' };
    }
};

/**
 * Date formatting (UK Standard)
 */
const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric'});
};

/**
 * Firestore Timestamp formatting
 */
const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return timestamp.toDate().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Countdown logic for compliance dates
 */
const getDaysRemaining = (dateStr) => {
    if (!dateStr) return '';
    const diff = new Date(dateStr) - new Date();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    if (days < 0) return `${Math.abs(days)} DAYS OVERDUE`;
    if (days === 0) return 'DUE TODAY';
    return `${days} DAYS REMAINING`;
};

/**
 * Compliance status coloring
 */
const getDateClass = (dateStr) => {
    if (!dateStr) return 'text-slate-300';
    const diff = new Date(dateStr) - new Date();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    
    if (days < 0) return 'text-rose-500';
    if (days < 30) return 'text-amber-500';
    return 'text-[#20FCF1]';
};

/**
 * Data Hydration Logic
 * Queries via the 'assignedVehicleReg' field in the User Profile
 */
const fetchData = async () => {
    try {
        const profile = userStore.userProfile;
        const cid = userStore.clientId || profile?.clientId;
        const assignedReg = profile?.assignedVehicleReg;
        
        // Critical Exit: No registration assigned to this profile
        if (!cid || !assignedReg) {
            loading.value = false;
            return;
        }

        // 1. Fetch Asset via Registration Link
        const vQuery = query(
            collection(db, 'clients', cid, 'vehicles'),
            where('registration', '==', assignedReg.toUpperCase().trim()),
            limit(1)
        );
        const vSnap = await getDocs(vQuery);
        
        if (!vSnap.empty) {
            const vDoc = vSnap.docs[0];
            vehicle.value = { id: vDoc.id, ...vDoc.data() };

            // 2. Fetch Recent Audit History for this asset
            const cQuery = query(
                collection(db, 'vehicleChecks'),
                where('vehicleReg', '==', assignedReg.toUpperCase().trim()),
                orderBy('submittedAt', 'desc'),
                limit(5)
            );
            
            try {
                const cSnap = await getDocs(cQuery);
                recentChecks.value = cSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            } catch (historyErr) {
                console.warn("Audit history fetch failure (Index potentially required):", historyErr);
            }
        }

    } catch (e) {
        console.error("Asset Hydration Failure:", e);
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    if (userStore.isAuthReady) fetchData();
    else {
        // Fallback for store hydration
        const unwatch = userStore.$subscribe(() => {
            if (userStore.isAuthReady) {
                fetchData();
                unwatch();
            }
        });
    }
});
</script>

<style scoped>
.micro-label { @apply block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]; }

.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

/* Asset Card Internal Styling */
.text-display-box {
    min-height: 12rem;
    max-height: 32rem;
    overflow-y: auto;
}
</style>